// Browser compatibility utilities
export const getBrowserInfo = () => {
  if (typeof navigator === 'undefined') {
    return {
      isIOS: false,
      isSafari: false,
      isMobileSafari: false,
      isChrome: false,
      isFirefox: false,
      isEdge: false,
      isOpera: false,
      isSamsung: false,
      isAndroid: false,
      isMobile: false,
      isTouch: false,
      userAgent: ''
    };
  }

  const userAgent = navigator.userAgent.toLowerCase();
  const isIOS = /ipad|iphone|ipod/.test(userAgent);
  const isAndroid = /android/.test(userAgent);
  const isSafari = /safari/.test(userAgent) && !/chrome/.test(userAgent) && !/chromium/.test(userAgent);
  const isMobileSafari = isIOS && isSafari;
  const isChrome = /chrome/.test(userAgent) && !/edge/.test(userAgent);
  const isFirefox = /firefox/.test(userAgent);
  const isEdge = /edge/.test(userAgent) || /edg/.test(userAgent);
  const isOpera = /opera/.test(userAgent) || /opr/.test(userAgent);
  const isSamsung = /samsungbrowser/.test(userAgent);
  const isMobile = /mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/.test(userAgent);
  const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  return {
    isIOS,
    isAndroid,
    isSafari,
    isMobileSafari,
    isChrome,
    isFirefox,
    isEdge,
    isOpera,
    isSamsung,
    isMobile,
    isTouch,
    userAgent
  };
};

// Cross-browser video autoplay handler
export const handleVideoAutoplay = (videoElement) => {
  if (!videoElement) return;
  
  const browser = getBrowserInfo();
  
  // Add Safari-specific attributes
  if (browser.isSafari || browser.isIOS) {
    videoElement.setAttribute('webkit-playsinline', 'true');
    videoElement.setAttribute('playsinline', 'true');
    videoElement.muted = true;
  }
  
  // Attempt to play with error handling
  const playPromise = videoElement.play();
  
  if (playPromise !== undefined) {
    playPromise.catch(error => {
      console.log('Video autoplay prevented:', error);
      // For iOS, we might need user interaction
      if (browser.isIOS) {
        // Add a play button overlay or handle user interaction
        videoElement.controls = false;
        videoElement.poster = videoElement.poster || '';
      }
    });
  }
};

// Get appropriate background attachment for mobile Safari
export const getBackgroundAttachment = () => {
  const browser = getBrowserInfo();
  // Mobile Safari has issues with background-attachment: fixed
  return browser.isMobileSafari ? 'scroll' : 'fixed';
};

// Cross-browser gradient text support
export const getGradientTextStyles = (gradient, fallbackColor = '#333') => {
  const browser = getBrowserInfo();
  
  // Provide better fallback for browsers that don't support gradient text
  const baseStyles = {
    color: fallbackColor
  };
  
  // Only apply gradient styles for supported browsers
  if (browser.isChrome || browser.isSafari || browser.isFirefox || browser.isEdge) {
    return {
      ...baseStyles,
      background: gradient,
      WebkitBackgroundClip: 'text',
      MozBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      MozTextFillColor: 'transparent',
      backgroundClip: 'text',
      textFillColor: 'transparent'
    };
  }
  
  return baseStyles;
};

// Detect if device prefers reduced motion
export const prefersReducedMotion = () => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// CSS feature detection
export const supportsCSSFeature = (property, value) => {
  if (typeof CSS !== 'undefined' && CSS.supports) {
    return CSS.supports(property, value);
  }
  
  // Fallback for older browsers
  const element = document.createElement('div');
  element.style[property] = value;
  return element.style[property] === value;
};

// Check for CSS Grid support
export const supportsGrid = () => {
  return supportsCSSFeature('display', 'grid');
};

// Check for CSS Flexbox support
export const supportsFlexbox = () => {
  return supportsCSSFeature('display', 'flex');
};

// Get CSS variables with fallback
export const getCSSVariable = (variable, fallback) => {
  if (typeof getComputedStyle !== 'undefined') {
    const value = getComputedStyle(document.documentElement).getPropertyValue(variable);
    return value.trim() || fallback;
  }
  return fallback;
};

// Fix for viewport units on mobile
export const getViewportHeight = () => {
  const browser = getBrowserInfo();
  
  if (browser.isMobile && window.visualViewport) {
    return window.visualViewport.height;
  }
  
  return window.innerHeight;
};

// Get safe area insets for notched devices
export const getSafeAreaInsets = () => {
  return {
    top: getCSSVariable('--safe-area-inset-top', '0px'),
    right: getCSSVariable('--safe-area-inset-right', '0px'),
    bottom: getCSSVariable('--safe-area-inset-bottom', '0px'),
    left: getCSSVariable('--safe-area-inset-left', '0px')
  };
};

// Responsive video attributes for cross-browser compatibility
export const getVideoAttributes = () => {
  const browser = getBrowserInfo();
  
  return {
    autoPlay: true, // Keep autoplay enabled for all devices
    loop: true,
    muted: true, // Required for autoplay to work
    playsInline: true, // Critical: prevents fullscreen on mobile
    'webkit-playsinline': 'true', // For older webkit browsers
    preload: 'metadata',
    disablePictureInPicture: true,
    controls: false,
    // iOS specific - enhanced to prevent fullscreen
    ...(browser.isIOS && {
      'playsinline': 'true',
      'webkit-playsinline': 'true',
      'x5-playsinline': 'true', // For WeChat browser on iOS
      'x-webkit-airplay': 'deny', // Prevent AirPlay
      'x5-video-player-fullscreen': 'false', // Prevent fullscreen
      'x5-video-orientation': 'portraint' // Lock orientation
    }),
    // Android and Samsung browser specific - enhanced to prevent fullscreen
    ...((browser.isAndroid || browser.isSamsung) && {
      'playsinline': 'true',
      'webkit-playsinline': 'true',
      'x5-video-player-type': 'h5', // For QQ/WeChat browsers
      'x5-video-player-fullscreen': 'false', // Prevent fullscreen in QQ browser
      'x-webkit-airplay': 'deny',
      'x5-video-orientation': 'portraint' // Lock orientation
    })
  };
};

// Cross-browser transform styles
export const getTransformStyles = (transform) => {
  return {
    transform,
    WebkitTransform: transform,
    MozTransform: transform,
    msTransform: transform,
    OTransform: transform
  };
};

// Cross-browser transition styles
export const getTransitionStyles = (transition) => {
  return {
    transition,
    WebkitTransition: transition,
    MozTransition: transition,
    msTransition: transition,
    OTransition: transition
  };
};